import type { MembershipStatus, MembershipVm } from '@core/profile/membership.service';

import type {
  HandelMembershipPageConfig,
  HandelMembershipPageContentByVariant,
} from '../../theme.types';

/** Cada variante tiene su propio contenido; se agrega una por cada audiencia nueva. */
export type MembresiasVariante =
  | 'premium-cortesia'
  | 'infinite-cortesia'
  | 'premium-pagada'
  | 'infinite-pagada'
  | 'general';

/** Clave de la variante en la configuración y en el contenido del tema. */
export type MembresiasClave = keyof HandelMembershipPageConfig &
  keyof HandelMembershipPageContentByVariant;

/** Las variantes del socio vigente, que se distinguen por plan y origen. */
type VarianteDeSocio = Exclude<MembresiasVariante, 'general'>;
type ClaveDeSocio = Exclude<MembresiasClave, 'general'>;

const CLAVE_DE_SOCIO: Readonly<Record<VarianteDeSocio, ClaveDeSocio>> = {
  'premium-cortesia': 'premiumCortesia',
  'infinite-cortesia': 'infiniteCortesia',
  'premium-pagada': 'premiumPagada',
  'infinite-pagada': 'infinitePagada',
};

export const CLAVE_POR_VARIANTE: Readonly<Record<MembresiasVariante, MembresiasClave>> = {
  ...CLAVE_DE_SOCIO,
  general: 'general',
};

const VARIANTES_DE_SOCIO = Object.keys(CLAVE_DE_SOCIO) as readonly VarianteDeSocio[];

/**
 * Una suscripción en mora sigue dando la membresía mientras el motor reintenta
 * el cobro; una suspendida o cancelada ya no.
 */
const ESTADOS_DE_SUSCRIPCION_VIGENTE: ReadonlySet<MembershipStatus> = new Set([
  'active',
  'past_due',
]);

type MembresiaDeAudiencia = Pick<MembershipVm, 'origen' | 'plan' | 'status'>;

export interface MembresiasAudiencia {
  readonly segmentCode: string | null;
  /** `null` cuando la cuenta no tiene membresía. */
  readonly membership: MembresiaDeAudiencia | null;
}

function estaVigente(membership: MembresiaDeAudiencia): boolean {
  if (membership.origen === 'otorgada') return true;
  return membership.status !== null && ESTADOS_DE_SUSCRIPCION_VIGENTE.has(membership.status);
}

function coincideSegmento(
  clave: ClaveDeSocio,
  segmentCode: string | null,
  config: HandelMembershipPageConfig,
): boolean {
  const esperado = config[clave].segmentCode;
  return esperado === null || esperado === segmentCode;
}

function variantePorMembresia(
  segmentCode: string | null,
  membership: MembresiaDeAudiencia,
  config: HandelMembershipPageConfig,
): VarianteDeSocio | null {
  return (
    VARIANTES_DE_SOCIO.find((variante) => {
      const clave = CLAVE_DE_SOCIO[variante];
      return (
        coincideSegmento(clave, segmentCode, config) &&
        config[clave].plan === membership.plan &&
        config[clave].origin === membership.origen
      );
    }) ?? null
  );
}

/**
 * Sin membresía —nunca la tuvo o la canceló y ya terminó— la página es la de
 * los planes que puede contratar. Una suspendida no entra: tiene un cobro que
 * resolver, no un plan que elegir.
 */
function esGeneral(
  segmentCode: string | null,
  membership: MembresiaDeAudiencia | null,
  config: HandelMembershipPageConfig,
): boolean {
  const sinPlan = membership === null || membership.status === 'cancelled';
  return sinPlan && segmentCode === config.general.segmentCode;
}

/** `null` significa que la página no le corresponde a este socio. */
export function resolverVariante(
  audiencia: MembresiasAudiencia,
  config: HandelMembershipPageConfig,
): MembresiasVariante | null {
  const { segmentCode, membership } = audiencia;
  if (membership && estaVigente(membership)) {
    return variantePorMembresia(segmentCode, membership, config);
  }
  return esGeneral(segmentCode, membership, config) ? 'general' : null;
}
